import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { parse, parseISO } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';

import Confirm from 'components/Confirm';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem, classNameWithModifiers as withMods } from 'helpers/class-name';

import { alternativeTimeConfirmationModalMessages as messages } from '../../Appointments.messages';
import { getFullDayStaffBlocksFromStartTimes } from './AlternativeTime.helpers';

import './AlternativeTimeConfirmationModal.scss';

const { block, element } = bem('alternative-time-confirmation-modal');

const TimeIterator = ({ times }) => {
  const intl = useIntl();

  return (
    <span className="text--sm text--semibold">
      {times.map(time => intl.formatTime(parse(time, 'HH:mm', new Date()))).join(', ')}
    </span>
  );
};

TimeIterator.propTypes = {
  times: PropTypes.arrayOf(PropTypes.string),
};

const AlternativeTimeConfirmationModal = ({ appointmentTime, clientMessage, payload, teacherName }) => {
  const { blockSelectedDays, startTimes } = payload;
  const staffBlocks = blockSelectedDays
    ? [appointmentTime, ...getFullDayStaffBlocksFromStartTimes(startTimes)]
    : [appointmentTime];

  const alternativeTimes = startTimes.reduce((acc, { times }) => acc.concat(times), []);
  const hasClientMessage = !_isEmpty(clientMessage);

  const scrollingDivRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (hasClientMessage) {
      const div = scrollingDivRef.current;
      if (div.scrollHeight > div.clientHeight) {
        setIsScrollable(true);
      } else {
        setIsScrollable(false);
      }
    }
  }, [clientMessage, scrollingDivRef]);

  return (
    <Confirm
      className={block()}
      cancelStyle="primary-outline"
      title={<Message message={messages.title} />}
      cancelNode={<Message message={messages.cancel} />}
      acceptNode={<Message message={messages.confirm} />}
      size="medium"
      isOpen>
      <div className={element('section')}>
        <div className={element('header-container')}>
          <Message as="p" className={element('header-text')} message={messages.suggestedTimesHeader} />
          <TooltipToggle name="alternative-time-tooltip" className={element('tooltip-toggle')}>
            <Isvg src={ICONS.infoGrey} className={element('info-icon')} />
          </TooltipToggle>
          <TooltipContent
            name="alternative-time-tooltip"
            className={element('tooltip-content')}
            hoverable
            place="right">
            <Message message={messages.overrideStaffBlocks} />
          </TooltipContent>
        </div>
        <When predicate={_isEmpty(alternativeTimes)}>
          <Message as="p" className="text--sm text--semibold" message={messages.noneSelected} />
        </When>
        <When predicate={!_isEmpty(alternativeTimes)}>
          <div className="grid grid--flush">
            <div className="grid__item 4/10">
              <Message className={element('label-text')} message={messages.dateLabel} />
            </div>
            <div className="grid__item 6/10">
              <Message className={element('label-text')} message={messages.timeLabel} />
            </div>
            {startTimes.map(({ date, times }) => (
              <When key={date} predicate={times.length > 0}>
                <div className="grid__item 4/10">
                  <Message
                    className="text--sm text--semibold"
                    format="medium-w-long-dow-no-year"
                    value={parseISO(date)}
                  />
                </div>
                <div className="grid__item 6/10">
                  <TimeIterator times={times} />
                </div>
              </When>
            ))}
          </div>
        </When>
      </div>
      <div className={element('section')}>
        <Message
          as="p"
          className={element('header-text')}
          message={messages.staffBlockHeader}
          values={{ numberOfBlocks: staffBlocks.length }}
        />
        {staffBlocks.map(staffBlock => {
          return (
            <div key={staffBlock.startDateTime} className={element('staff-block-section')}>
              <Message
                className="text--sm text--semibold pr-"
                message={messages.staffBlockLabel}
                values={{ startDateTime: staffBlock.startDateTime }}
              />
              <Message
                message={messages.staffBlock}
                values={{ teacherName, startDateTime: staffBlock.startDateTime, endDateTime: staffBlock.endDateTime }}
              />
            </div>
          );
        })}
      </div>
      <When predicate={hasClientMessage}>
        <div className={element('section')}>
          <Message as="p" className={element('header-text')} message={messages.clientMessageHeader} />
          <div
            className={withMods(element('client-message-section'), { scrollable: isScrollable })}
            ref={scrollingDivRef}>
            {clientMessage}
          </div>
        </div>
      </When>
    </Confirm>
  );
};

AlternativeTimeConfirmationModal.propTypes = {
  appointmentTime: PropTypes.shape({
    endDateTime: PropTypes.instanceOf(Date),
    startDateTime: PropTypes.instanceOf(Date),
  }),
  clientMessage: PropTypes.string,
  payload: PropTypes.shape({
    blockSelectedDays: PropTypes.bool,
    startTimes: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        times: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }),
  teacherName: PropTypes.string,
};

export default AlternativeTimeConfirmationModal;
